import {
  useState,
  useCallback,
  useRef,
  useEffect,
  createContext,
  useContext,
  createElement,
} from "react";
import type { ReactNode } from "react";

import type { ToastVariant } from "@/components/Toast";

export interface ToastOptions {
  duration?: number;
  variant?: ToastVariant;
}

interface ToastContextValue {
  message: string;
  visible: boolean;
  variant: ToastVariant;
  showToast: (message: string, options?: ToastOptions) => void;
  hideToast: () => void;
}

interface ToastProviderProps {
  children: ReactNode;
  defaultDuration?: number;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Use at root of application so that Toasts can be rendered anywhere */
export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  defaultDuration = 5000,
}) => {
  const [message, setMessage] = useState("");
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState<ToastVariant>("error");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hideToast = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisible(false);
    // Wait for animation to complete before clearing message
    setTimeout(() => setMessage(""), 300);
  }, []);

  const showToast = useCallback(
    (newMessage: string, options?: ToastOptions) => {
      const duration = options?.duration ?? defaultDuration;
      const nextVariant = options?.variant ?? "error";

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new message and show toast
      setMessage(newMessage);
      setVariant(nextVariant);
      setVisible(true);

      // Auto-dismiss after duration
      timeoutRef.current = setTimeout(() => {
        hideToast();
      }, duration);
    },
    [defaultDuration, hideToast],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const value: ToastContextValue = {
    message,
    visible,
    variant,
    showToast,
    hideToast,
  };

  return createElement(ToastContext.Provider, { value }, children);
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
